import { AUJS_FORMS } from '@app/forms/shared/aujs-forms';
import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import { AppSetting } from '@app/settings/shared/setting.model';
import { DispatchService } from '@app/dispatcher/dispatch.service';
import { IAppSetting } from '@app/settings/shared/setting.interface';
import { IReportSettingsManager } from '@app/reports/report-settings/report-settings-manager.interface';
import { LegacyService } from '@app/legacy/legacy.service';
import { LoginStatusService } from '@app/security/shared/login-status.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { QuickFormOption } from '@app/reports/report-settings/shared/quick-form-option.model';
import { ReportSettingsService } from '@app/reports/report-settings/report-settings.service';
import { SecurityService } from '@app/security/shared/security.service';
import { SettingDialogComponent } from '@app/ui/shared/setting-dialog.component';
import { SettingsService } from '@app/settings/shared/settings.service';
import { Subscription } from 'rxjs';
import { getIdentifierForIterableItem } from '@app/shared/trackby.service';
import { hasValue } from '@app/shared/comparison-helpers.service';

const toolbarSettingId = 'tlbr:QuickLaunchForms';

@Component({
  selector: 'acs-toolbar',
  templateUrl: 'toolbar.component.html'
})
export class ToolbarComponent implements AfterViewInit, OnDestroy {
  public getIdentifierForIterableItem = getIdentifierForIterableItem;
  public loginObserver: Subscription;
  public selectedFormIds: Array<number>;
  public useLongName: boolean;
  public userLoggedIn: boolean;
  public userSettings: IReportSettingsManager;

  private quickLaunchSetting: IAppSetting<any>;
  private settingDialog: MatDialogRef<SettingDialogComponent>;

  constructor(
    public matDialog: MatDialog,
    private appSettingsService: SettingsService,
    private dispatchService: DispatchService,
    private legacyService: LegacyService,
    private loginStatusService: LoginStatusService,
    private reportSettingsService: ReportSettingsService,
    private securityService: SecurityService
  ) {
    this.selectedFormIds = [];
    this.useLongName = false;
    this.userLoggedIn = this.securityService.getUserLoggedIn();

    this.userSettings = this.reportSettingsService.getNewReportSettings();
    this.userSettings.addSetting(this.reportSettingsService.getQuickFormsSetting());

    this.loginObserver = this.loginStatusService.loginStatus.subscribe((loginStatus) => {
      this.userLoggedIn = loginStatus.loggedIn;
      if (this.userLoggedIn) {
        this.appSettingsService
          .getSettingById(toolbarSettingId)
          .subscribe((setting: IAppSetting<any>) => {
            this.onQuickLaunchSettingRetrieved(setting);
          });
      } else {
        this.userSettings.clean();
        this.userSettings.addSetting(this.reportSettingsService.getQuickFormsSetting());
      }
    });

    if (this.userLoggedIn) {
      this.appSettingsService
        .getSettingById(toolbarSettingId)
        .subscribe((setting: IAppSetting<any>) => {
          this.onQuickLaunchSettingRetrieved(setting);
        });
    }
  }

  public ngAfterViewInit(): void {
    this.dispatchService.quickLaunchEvents.subscribe((quickLaunchUpdate) => {
      if (quickLaunchUpdate.data.username === this.securityService.getUsername()) {
        this.userSettings['forms'].loadAppSettingValue(quickLaunchUpdate.data.formIds);
        this.classicSettingsFilterCheck();
        const selectedForms = this.userSettings['forms'].options.filter(
          (option: QuickFormOption) => option.selected === true
        );

        this.selectedFormIds = [];

        this.selectedFormIds = selectedForms.map((f) => f.id);
        const names = selectedForms.map((option: any) => {
          return AUJS_FORMS[option.id].name;
        });

        this.useLongName =
          names.length !== names.filter((val, i, self) => self.indexOf(val) === i).length;

        this.quickLaunchSetting.data = this.userSettings['forms'].getAppSettingValue();
      }
    });
  }

  public ngOnDestroy(): void {
    this.loginObserver.unsubscribe();
  }

  public showSettings(): void {
    this.settingDialog = this.matDialog.open(SettingDialogComponent, {
      data: {
        name: 'User Settings',
        settings: this.userSettings
      },
      height: '70vh',
      maxHeight: '95vh',
      maxWidth: '1200px',
      width: '85vw',
      panelClass: 'acs-dialog-non-padded',
      autoFocus: true
    });

    this.settingDialog.afterClosed().subscribe((settings: IReportSettingsManager) => {
      if (hasValue(settings)) {
        this.userSettings = settings;
        this.updateDisplayedForms();
      }
    });
  }

  public logout(): void {
    this.securityService.logout();
  }

  /************************
   * Private
   ***********************/

  private onQuickLaunchSettingRetrieved(quickLaunchSettingFromServer: IAppSetting<any>): void {
    this.userSettings['forms'].whenReady().subscribe(() => {
      if (
        typeof quickLaunchSettingFromServer !== 'undefined' &&
        quickLaunchSettingFromServer !== null
      ) {
        this.quickLaunchSetting = quickLaunchSettingFromServer;
        this.userSettings['forms'].loadAppSettingValue(this.quickLaunchSetting.data);
        this.updateDisplayedForms();
      } else {
        this.userSettings['forms'].loadAppSettingValue([
          'FormOrderEntryConcreteBasic',
          'FormTicketConcrete',
          'FormBatchLoadScreen'
        ]);
        this.quickLaunchSetting = new AppSetting(
          toolbarSettingId,
          this.userSettings['forms'].getAppSettingValue()
        );
        this.appSettingsService.saveSetting(this.quickLaunchSetting).subscribe(() => {
          this.updateDisplayedForms();
        });
      }
    });
  }

  private updateDisplayedForms(): void {
    const selectedForms = this.userSettings['forms'].options.filter(
      (option: QuickFormOption) => option.selected === true
    );

    this.selectedFormIds = selectedForms.map((f) => f.id);
    const names = selectedForms.map((option: any) => {
      return AUJS_FORMS[option.id].name;
    });

    this.useLongName =
      names.length !== names.filter((val, i, self) => self.indexOf(val) === i).length;

    this.quickLaunchSetting.data = this.userSettings['forms'].getAppSettingValue();
    this.appSettingsService.updateSetting(this.quickLaunchSetting).subscribe();
  }

  private classicSettingsFilterCheck(): void {
    if (this.userSettings.forms?.type === 'multiselect' && this.legacyService.hideLegacyForms) {
      const dataToFilter = this.userSettings.forms.options;

      if (dataToFilter?.length > 0) {
        this.userSettings.forms.options = dataToFilter.filter(
          (option: QuickFormOption) => !option.legacy
        );
      }
    }
  }
}
